import { getSupabase } from '$db/remote/supabase';
import { getActiveUserId } from '$core/identity/active-user';
import { nowIso } from '$db/local/repositories/common';
import type { PushSubscriptionInput, PushSubscriptionRecord } from './types';

/**
 * Cliente de push_subscriptions. Va directo a Supabase, sin bridge de sync.
 *
 * Motivo: la suscripción push requiere red (el permiso del navegador la
 * necesita y la Edge Function es remota). Si no hay red, el usuario ve un
 * error y reintenta. No es una operación crítica offline.
 */

export async function savePushSubscription(
  input: PushSubscriptionInput
): Promise<PushSubscriptionRecord | null> {
  const client = getSupabase();
  if (!client) return null;

  const now = nowIso();
  const userId = getActiveUserId();

  const { data, error } = await client
    .from('push_subscriptions')
    .upsert(
      {
        user_id: userId,
        endpoint: input.endpoint,
        p256dh: input.p256dh,
        auth: input.auth,
        user_agent: input.userAgent,
        reminder_hour: input.reminderHour,
        timezone_offset_minutes: input.timezoneOffsetMinutes,
        updated_at: now
      },
      { onConflict: 'user_id,endpoint' }
    )
    .select()
    .single();

  if (error) {
    if (import.meta.env.DEV) {
      console.warn('[StudyQuantifier] savePushSubscription failed:', error.message);
    }
    return null;
  }

  return mapRecord(data);
}

export async function updatePushReminder(
  reminderHour: number,
  timezoneOffsetMinutes: number
): Promise<boolean> {
  const client = getSupabase();
  if (!client) return false;
  const userId = getActiveUserId();
  const { error } = await client
    .from('push_subscriptions')
    .update({
      reminder_hour: reminderHour,
      timezone_offset_minutes: timezoneOffsetMinutes,
      updated_at: nowIso()
    })
    .eq('user_id', userId);

  if (error) {
    if (import.meta.env.DEV) {
      console.warn('[StudyQuantifier] updatePushReminder failed:', error.message);
    }
    return false;
  }
  return true;
}

export async function deletePushSubscription(endpoint: string): Promise<boolean> {
  const client = getSupabase();
  if (!client) return false;

  const userId = getActiveUserId();
  const { error } = await client
    .from('push_subscriptions')
    .delete()
    .eq('user_id', userId)
    .eq('endpoint', endpoint);

  if (error) {
    if (import.meta.env.DEV) {
      console.warn('[StudyQuantifier] deletePushSubscription failed:', error.message);
    }
    return false;
  }
  return true;
}

export async function listMyPushSubscriptions(): Promise<PushSubscriptionRecord[]> {
  const client = getSupabase();
  if (!client) return [];

  const userId = getActiveUserId();
  const { data, error } = await client
    .from('push_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error || !data) {
    if (error && import.meta.env.DEV) {
      console.warn('[StudyQuantifier] listMyPushSubscriptions failed:', error.message);
    }
    return [];
  }

  return data.map(mapRecord);
}

interface SupabaseRow {
  id: string;
  user_id: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  user_agent: string | null;
  created_at: string;
  updated_at: string;
}

function mapRecord(row: SupabaseRow): PushSubscriptionRecord {
  return {
    id: row.id,
    userId: row.user_id,
    endpoint: row.endpoint,
    p256dh: row.p256dh,
    auth: row.auth,
    userAgent: row.user_agent,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}